import { useAlert } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInRootStackParamList } from '../../../routes/types';
import { useBlockListActions } from '../../../store/blockList/actions';
import { blockListUnlockCustomerSendState, searchTermState } from '../../../store/blockList/atom';
import { customersLockListQuery } from '../../../store/blockList/selector';

import { ContainerDesktop } from './Desktop';
import { ContainerMobile } from './Mobile';

export namespace UnlockCustomerTypes {
  export type Props = {
    handleCheckboxChange: (value: string, selected: boolean) => void;
    handleSwitchChange: (value: string) => void;
    handleTextAreaChange: (value: string) => void;
    selectedValues: string[];
    textAreaInput: string;
    chosenShippingType: boolean;
    sendUnlockCustomer: () => void;
    unlockCustomerSendState: { isSend: boolean; isError: boolean; isLoading: boolean };
    goBack: () => void;
  };
}

export const UnlockCustomer: React.FC<
  NativeStackScreenProps<SignedInRootStackParamList, 'UnlockCustomer'>
> = ({ route }) => {
  const { customerId, optoutId } = route.params;
  const navigation = useNavigation();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const { alert } = useAlert();

  const { showToast, showToastFromError } = useToastCreator('unlock-toast');
  const { sendUnlock } = useBlockListActions();

  const [selectedValues, setSelectedValues] = useState<string[]>(['email']);

  const [textAreaInput, setTextAreaInput] = useState('');

  const handleCheckboxChange = (value: string, selected: boolean) => {
    setSelectedValues(
      selected ? [...selectedValues, value] : selectedValues.filter((val) => val !== value)
    );
  };

  const handleSwitchChange = (value: string) => {
    const valueExists = selectedValues.includes(value);

    setSelectedValues((prevValues) =>
      valueExists ? prevValues.filter((val) => val !== value) : [...prevValues, value]
    );
  };

  const chosenShippingType = selectedValues.includes('email');

  const handleTextAreaChange = (input: string) => {
    setTextAreaInput(input);
  };

  const getSearchTerm = useRecoilValue(searchTermState);

  const { refresher } = useRecoilQuery(customersLockListQuery({ searchTerm: getSearchTerm }), {
    cachePolicy: 'no-cache',
  });

  const unlockCustomerSendState = useRecoilValue(blockListUnlockCustomerSendState);

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  useEffect(() => {
    if (unlockCustomerSendState.isSend) {
      goBack();
      refresher();

      if (!isMobile) {
        showToast(
          'success',
          'Tudo certo!',
          'Solicitação enviada com sucesso! O link enviado é de uso único e expirará após a primeira tentativa de acesso.',
          {
            duration: 3000,
          }
        );
      } else {
        alert(
          {
            icon: 'BackInTime',
            description:
              'Solicitação enviada com sucesso! O link enviado é de uso único e expirará após a primeira tentativa de acesso.',
          },
          () => {}
        );
      }
    } else if (unlockCustomerSendState.isError) {
      if (!isMobile) {
        showToastFromError({
          errorMessage: 'Ocorreu um erro!',
          errorSuggest: 'Por favor, tente novamente mais tarde.',
        });
      } else {
        alert(
          {
            isMobile,
            icon: 'BackInTime',
            description: 'Ocorreu um erro! Por favor, tente novamente mais tarde.',
          },
          () => {}
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockCustomerSendState]);

  const sendUnlockCustomer = () => {
    if (customerId) {
      sendUnlock({
        customerId: parseInt(customerId, 10),
        channel: 'EMAIL',
        observation: textAreaInput,
        optoutId,
      });
    }
  };

  return useBreakpointValue({
    base: (
      <ContainerMobile
        unlockCustomerSendState={unlockCustomerSendState}
        chosenShippingType={chosenShippingType}
        handleCheckboxChange={handleCheckboxChange}
        handleSwitchChange={handleSwitchChange}
        handleTextAreaChange={handleTextAreaChange}
        selectedValues={selectedValues}
        textAreaInput={textAreaInput}
        sendUnlockCustomer={sendUnlockCustomer}
        goBack={goBack}
      />
    ),
    sm: (
      <ContainerDesktop
        unlockCustomerSendState={unlockCustomerSendState}
        chosenShippingType={chosenShippingType}
        handleCheckboxChange={handleCheckboxChange}
        handleSwitchChange={handleSwitchChange}
        handleTextAreaChange={handleTextAreaChange}
        selectedValues={selectedValues}
        textAreaInput={textAreaInput}
        sendUnlockCustomer={sendUnlockCustomer}
        goBack={goBack}
      />
    ),
  });
};
